/**
 * Core states will be in global scope even they are in core state module (i.e. not in the
 * isolated namespace) as they should be always appllication-level data accessed by various
 * feature modules.
 *
 */
import { Module } from 'vuex';
import { RootStoreState } from '@/store';
import { appConfigService, AppConfig } from './app-config-service';
import zeroDayAttachment from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_0day_Attachments.svg';
import zeroDayThreatsOffNetwork from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_0day_Threats_Off-Network.svg';
import zeroDayThreats from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_0day_Threats.svg';
import cloudDataLoss from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_Cloud_Data_Loss.svg';
import intrusionsOffNetwork from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_Intrusions_Off-Network.svg';
import malwareOffNetwork from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_Malware_Off-Network.svg';
import malwareOnCloud from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_Malware_on_Cloud.svg';
import malware from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_Malware.svg';
import phishing from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_Phishing.svg';
import ransomwareOffNetwork from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_Ransomware_Off-Network.svg';
import undetectableThreats from '@/modules/sw-ftr-bill-meter/assets/icon_risk_meters/Icon_Undetectable_Threats.svg';
export interface Threat {
  id: number;
  show: boolean;
  layer:number;
  icon:string;
  color:string;
  magnitude:number;
  isPenetrating:boolean;
  isCursorOver: boolean;
  iconName: string;
  far: number;
  title: string;
  innerLayer?: number;
  SSCV2ArrayIndex: number;
  svg: boolean;
  image?: typeof import('*.svg');
  globalIndex: number;
}
export interface CoreStoreState {
  viewMode: string;
  circleMenuForcePos: { left: number, top: number} | null;
  appConfig: AppConfig;
  navCollapsed: boolean;
  selectedNavItemId: string;
  defcon: number;
  defconPrecise: number;
  defconWhatIf: number;
  defconPreciseWhatIf: number;
  token: string | undefined;
  code: string | undefined;
  exchangeAccessCode: boolean;
  tenantid: string | undefined;
  username: string | undefined;
  inEditMode: boolean;
  loadDetails: boolean;
  layerOneInnerLayersActivated: boolean[];
  layerTwoInnerLayersActivated: boolean[];
  layerThreeInnerLayersActivated: boolean[];
  layerOneInnerLayersActivatedWhatIf: boolean[];
  layerTwoInnerLayersActivatedWhatIf: boolean[];
  layerThreeInnerLayersActivatedWhatIf: boolean[];
  threats: Threat[];
  threatsWhatIf: Threat[];
  weights: any;
  SSCV2: any;
  mysonicwall: any;
  malware: any;
  thirdParty: any;
  billmeterloading: boolean;
  selectedTenantId: string;
  tenants: any;
  consolidatedThreats: any;
  rotations: number;
  bmResetAllowed: boolean;
  bmStartTime: number;
  bmError: boolean;
  showResponsiveNavMenu: boolean;
  waf: string;
  email: string;
  cloud: string;
  nextgenav: string;
  firewall: string;
  threatPercent: number;
  whatIfThreatPercent: number;
}

export default {
  state: {
    threats: [
      {
        id: 0,
        title: 'Malware',
        show: true,
        layer: 3,
        icon: '\uf1ae',
        iconName: 'threat-spyware',
        color: '#18B7A4',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        innerLayer: 2,
        SSCV2ArrayIndex: 1,
        svg: true,
        image: malware,
        globalIndex: -1,
      },
      {
        id: 1,
        title: 'Intrusion',
        show: true,
        layer: 3,
        icon: '\uf1ac',
        iconName: 'threat-intrusion',
        color: '#CE8ABC',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        innerLayer: 2,
        SSCV2ArrayIndex: 4,
        svg: false,
        globalIndex: -1,
      },
      {
        id: 2,
        title: 'Ransomware',
        show: false,
        layer: 3,
        icon: '\uf1f6',
        iconName: 'unique-ransomware',
        color: '#E2509A',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        innerLayer: 1,
        // SSCV2ArrayIndex: 3,
        SSCV2ArrayIndex: -1,
        svg: false,
        globalIndex: -1,
      },
      {
        id: 3,
        title: '0 Day Threats',
        show: true,
        layer: 3,
        icon: '\uf1ac',
        iconName: 'threat-intrusion',
        color: '#7A7EB4',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        innerLayer: 1,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: zeroDayThreats,
        globalIndex: -1,
      },
      {
        id: 4,
        title: 'Web App Attacks',
        show: true,
        layer: 3,
        icon: '\ue920',
        iconName: 'service-waf',
        color: '#68BBE7',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        innerLayer: 0,
        SSCV2ArrayIndex: 5,
        svg: false,
        globalIndex: -1,
      },
      {
        id: 5,
        title: 'Phishing',
        show: true,
        layer: 2,
        icon: '\uF00C',
        iconName: 'user-profile',
        color: '#BADB77',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: phishing,
        globalIndex: -1,
        innerLayer: 0,
      },
      {
        id: 6,
        title: 'Malicious Attachments',
        show: true,
        layer: 2,
        icon: '\uF032',
        iconName: 'android',
        color: '#609af7',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1, // 6,
        svg: true,
        image: zeroDayAttachment,
        globalIndex: -1,
        innerLayer: 0,
      },
      {
        id: 7,
        title: 'Malicious URLs',
        show: false,
        layer: 2,
        icon: '\uf1ab',
        iconName: 'url',
        color: 'red',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: 8,
        svg: false,
        globalIndex: -1,
        innerLayer: 0,
      },
      {
        id: 8,
        title: 'Malware on Cloud',
        show: true,
        layer: 2,
        icon: '\uEf1C',
        iconName: 'tux',
        color: '#80D8E0',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: malwareOnCloud,
        globalIndex: -1,
        innerLayer: 1,
      },
      {
        id: 9,
        title: 'Cloud Data Loss',
        show: true,
        layer: 2,
        icon: '\uf1ae',
        iconName: 'virtual-assist',
        color: '#18B7A4',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: cloudDataLoss,
        globalIndex: -1,
        innerLayer: 1,
      },
      {
        id: 10,
        title: 'Endpoint Malware',
        show: true,
        layer: 1,
        icon: '\uf1ae',
        iconName: 'virtual-assist',
        color: '#f9ae45',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: malwareOffNetwork,
        globalIndex: -1,
        innerLayer: 0,
      },
      {
        id: 11,
        title: 'Endpoint Exploits',
        show: true,
        layer: 1,
        icon: '\uf1ae',
        iconName: 'virtual-assist',
        color: '#e445f9',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: intrusionsOffNetwork,
        globalIndex: -1,
        innerLayer: 0,
      },
      {
        id: 12,
        title: 'Ransomware (Off Network)',
        show: false,
        layer: 1,
        icon: '\uf1ae',
        iconName: 'virtual-assist',
        color: '#45f94b',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: ransomwareOffNetwork,
        globalIndex: -1,
      },
      {
        id: 13,
        title: '0 Day Threats (ATP)',
        show: true,
        layer: 1,
        icon: '\uf1ae',
        iconName: 'virtual-assist',
        color: '#ff4470',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: zeroDayThreatsOffNetwork,
        globalIndex: -1,
        innerLayer: 1,
      },
      {
        id: 14,
        title: 'Rollbacks',
        show: true,
        layer: 1,
        icon: '\ue908',
        iconName: 'rollback',
        color: '#4683fc',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: undetectableThreats,
        globalIndex: -1,
        innerLayer: 1,
      },
    ],
    threatsWhatIf: [
      {
        id: 0,
        title: 'Malware',
        show: true,
        layer: 3,
        icon: '\uf1ae',
        iconName: 'threat-spyware',
        color: '#18B7A4',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        innerLayer: 2,
        SSCV2ArrayIndex: 1,
        svg: true,
        image: malware,
        globalIndex: -1,
      },
      {
        id: 1,
        title: 'Intrusion',
        show: true,
        layer: 3,
        icon: '\uf1ac',
        iconName: 'threat-intrusion',
        color: '#CE8ABC',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        innerLayer: 2,
        SSCV2ArrayIndex: 4,
        svg: false,
        globalIndex: -1,
      },
      {
        id: 2,
        title: 'Ransomware',
        show: false,
        layer: 3,
        icon: '\uf1f6',
        iconName: 'unique-ransomware',
        color: '#E2509A',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        innerLayer: 1,
        // SSCV2ArrayIndex: 3,
        SSCV2ArrayIndex: -1,
        svg: false,
        globalIndex: -1,
      },
      {
        id: 3,
        title: '0 Day Threats',
        show: true,
        layer: 3,
        icon: '\uf1ac',
        iconName: 'threat-intrusion',
        color: '#7A7EB4',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        innerLayer: 1,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: zeroDayThreats,
        globalIndex: -1,
      },
      {
        id: 4,
        title: 'Web App Attacks',
        show: true,
        layer: 3,
        icon: '\ue920',
        iconName: 'service-waf',
        color: '#68BBE7',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        innerLayer: 0,
        SSCV2ArrayIndex: 5,
        svg: false,
        globalIndex: -1,
      },
      {
        id: 5,
        title: 'Phishing',
        show: true,
        layer: 2,
        icon: '\uF00C',
        iconName: 'user-profile',
        color: '#BADB77',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: phishing,
        globalIndex: -1,
        innerLayer: 0,
      },
      {
        id: 6,
        title: 'Malicious Attachments',
        show: true,
        layer: 2,
        icon: '\uF032',
        iconName: 'android',
        color: '#609af7',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1, // 6,
        svg: true,
        image: zeroDayAttachment,
        globalIndex: -1,
        innerLayer: 0,
      },
      {
        id: 7,
        title: 'Malicious URLs',
        show: false,
        layer: 2,
        icon: '\uf1ab',
        iconName: 'url',
        color: 'red',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: 8,
        svg: false,
        globalIndex: -1,
        innerLayer: 0,
      },
      {
        id: 8,
        title: 'Malware on Cloud',
        show: true,
        layer: 2,
        icon: '\uEf1C',
        iconName: 'tux',
        color: '#80D8E0',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: malwareOnCloud,
        globalIndex: -1,
        innerLayer: 1,
      },
      {
        id: 9,
        title: 'Cloud Data Loss',
        show: true,
        layer: 2,
        icon: '\uf1ae',
        iconName: 'virtual-assist',
        color: '#18B7A4',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: cloudDataLoss,
        globalIndex: -1,
        innerLayer: 1,
      },
      {
        id: 10,
        title: 'Endpoint Malware',
        show: true,
        layer: 1,
        icon: '\uf1ae',
        iconName: 'virtual-assist',
        color: '#f9ae45',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: malwareOffNetwork,
        globalIndex: -1,
        innerLayer: 0,
      },
      {
        id: 11,
        title: 'Endpoint Exploits',
        show: true,
        layer: 1,
        icon: '\uf1ae',
        iconName: 'virtual-assist',
        color: '#e445f9',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: intrusionsOffNetwork,
        globalIndex: -1,
        innerLayer: 0,
      },
      {
        id: 12,
        title: 'Ransomware (Off Network)',
        show: false,
        layer: 1,
        icon: '\uf1ae',
        iconName: 'virtual-assist',
        color: '#45f94b',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: ransomwareOffNetwork,
        globalIndex: -1,
      },
      {
        id: 13,
        title: '0 Day Threats (ATP)',
        show: true,
        layer: 1,
        icon: '\uf1ae',
        iconName: 'virtual-assist',
        color: '#ff4470',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: zeroDayThreatsOffNetwork,
        globalIndex: -1,
        innerLayer: 1,
      },
      {
        id: 14,
        title: 'Rollbacks',
        show: true,
        layer: 1,
        icon: '\ue908',
        iconName: 'rollback',
        color: '#4683fc',
        magnitude: 40,
        isPenetrating: false,
        isCursorOver: false,
        far: 0,
        SSCV2ArrayIndex: -1,
        svg: true,
        image: undetectableThreats,
        globalIndex: -1,
        innerLayer: 1,
      },
    ],
    viewMode: 'self-contain',
    circleMenuForcePos: null,
    appConfig: {
      helpConfig: {
        url: '',
        tooltip: undefined,
        target: undefined,
      },
      loginConfig: {
        url: '',
        tooltip: undefined,
        target: undefined,
      },
      pagesConfig: [],
    },
    navCollapsed: false,
    selectedNavItemId: '',
    defcon: 0,
    defconPrecise: 0,
    defconWhatIf: 0,
    defconPreciseWhatIf: 0,
    token: undefined,
    code: undefined,
    exchangeAccessCode: false,
    tenantid: undefined,
    username: undefined,
    weights: undefined,
    SSCV2: undefined,
    mysonicwall: undefined,
    malware: undefined,
    billmeterloading: true,
    inEditMode: false,
    loadDetails: false,
    selectedTenantId: '',
    thirdParty: {},
    layerOneInnerLayersActivated: [true, true],
    layerTwoInnerLayersActivated: [true, true],
    layerThreeInnerLayersActivated: [true, true, true],
    layerOneInnerLayersActivatedWhatIf: [true, true],
    layerTwoInnerLayersActivatedWhatIf: [true, true],
    layerThreeInnerLayersActivatedWhatIf: [true, true, true],
    tenants: undefined,
    consolidatedThreats: undefined,
    rotations: 8,
    bmResetAllowed: false,
    bmStartTime: -1,
    showResponsiveNavMenu: false,
    bmError: false,
    waf: '',
    email: '',
    cloud: '',
    nextgenav: '',
    firewall: '',
    threatPercent: 0,
    whatIfThreatPercent: 0,
  },
  getters: {
    isAppConfigReady(state): boolean {
      return (state.appConfig.pagesConfig && state.appConfig.pagesConfig.length > 0);
    },
    isEmbeddedViewMode(state): boolean {
      return state.viewMode === 'embedded';
    },
    isStandAloneMode(state): boolean {
      return state.viewMode === 'standalone';
    },
    isCircleMenuMode(state): boolean {
      return state.viewMode === 'circlemenu';
    },
    getToggledLayerOneState(state): boolean[] {
      return state.layerOneInnerLayersActivated;
    },
    getToggledLayerTwoState(state): boolean[] {
      return state.layerTwoInnerLayersActivated;
    },
    getToggledLayerThreeState(state): boolean[] {
      return state.layerThreeInnerLayersActivated;
    },
  },
  mutations: {
    setBmError(state, playload) {
      state.bmError = playload.bmError;
    },
    setToggleLayerOneActivated(state, playload) {
      state.layerOneInnerLayersActivated = playload.layerOneInnerLayersActivated;
    },
    setToggleLayerTwoActivated(state, playload) {
      state.layerTwoInnerLayersActivated = playload.layerTwoInnerLayersActivated;
    },
    setToggleLayerThreeActivated(state, playload) {
      state.layerThreeInnerLayersActivated = playload.layerThreeInnerLayersActivated;
    },
    setToggleLayerOneActivatedWhatIf(state, playload) {
      state.layerOneInnerLayersActivatedWhatIf = playload.layerOneInnerLayersActivatedWhatIf;
    },
    setToggleLayerTwoActivatedWhatIf(state, playload) {
      state.layerTwoInnerLayersActivatedWhatIf = playload.layerTwoInnerLayersActivatedWhatIf;
    },
    setToggleLayerThreeActivatedWhatIf(state, playload) {
      state.layerThreeInnerLayersActivatedWhatIf = playload.layerThreeInnerLayersActivatedWhatIf;
    },
    toggleShowResponsiveNavMenu(state) {
      state.showResponsiveNavMenu = !state.showResponsiveNavMenu;
    },
    setBmStartTime(state, playload) {
      state.bmStartTime = playload.bmStartTime;
    },
    setBmResetAllowed(state, playload) {
      state.bmResetAllowed = playload.bmResetAllowed;
    },
    setRotations(state, playload) {
      state.rotations = playload.rotations;
    },
    setConsolidatedThreats(state, payload) {
      state.consolidatedThreats = payload.consolidatedThreats;
    },
    setThreats(state, payload) {
      state.threats = payload.threats;
    },
    setThreatsWhatIf(state, payload) {
      state.threatsWhatIf = payload.threatsWhatIf;
    },
    setTenants(state, payload) {
      state.tenants = payload.tenants;
    },
    setSelectedTenantId(state, payload) {
      state.selectedTenantId = payload.selectedTenantId;
    },
    setThirdParty(state, payload) {
      state.thirdParty = payload.thirdParty;
    },
    setBillmeterloading(state, payload) {
      state.billmeterloading = payload.billmeterloading;
    },
    setDetailMode(state, payload) {
      state.loadDetails = payload.loadDetails;
    },
    setNavMode(state, payload) {
      state.inEditMode = payload.inEditMode;
    },
    setWeights(state, payload) {
      state.weights = payload.weights;
    },
    setSSCV2(state, payload) {
      state.SSCV2 = payload.SSCV2;
    },
    setMysonicwall(state, payload) {
      state.mysonicwall = payload.mysonicwall;
    },
    setMalware(state, payload) {
      state.malware = payload.malware;
    },
    setDefconPrecise(state, payload) {
      state.defconPrecise = payload.defconPrecise;
    },
    setDefcon(state, payload) {
      state.defcon = payload.defcon;
    },
    setDefconPreciseWhatIf(state, payload) {
      state.defconPreciseWhatIf = payload.defconPreciseWhatIf;
    },
    setDefconWhatIf(state, payload) {
      state.defconWhatIf = payload.defconWhatIf;
    },
    setWAF(state, payload) {
      state.waf = payload.waf;
    },
    setEmail(state, payload) {
      state.email = payload.email;
    },
    setCloud(state, payload) {
      state.cloud = payload.cloud;
    },
    setNextGenAV(state, payload) {
      state.nextgenav = payload.nextgenav;
    },
    setFirewall(state, payload) {
      state.firewall = payload.firewall;
    },
    setToken(state, payload) {
      state.token = payload.token;
    },
    setCode(state, payload) {
      state.code = payload.code;
    },
    setExchangeAccessCode(state, payload) {
      state.exchangeAccessCode = payload.exchangeAccessCode;
    },
    setTenantid(state, payload) {
      state.tenantid = payload.tenantid;
    },
    setUsername(state, payload) {
      state.username = payload.username;
    },
    setViewMode(state, payload) {
      state.viewMode = payload.viewMode;
    },
    setCircleMenuPos(state, pos: CoreStoreState['circleMenuForcePos']) {
      state.circleMenuForcePos = pos;
    },
    selectNavItem(state, payload) {
      state.selectedNavItemId = payload.navId;
    },
    toggleNav(state, payload) {
      state.navCollapsed = payload ? payload.collapse : !state.navCollapsed;
    },
    setThreatPercent(state, payload) {
      state.threatPercent = payload.threatPercent;
    },
    setWhatIfThreatPercent(state, payload) {
      state.whatIfThreatPercent = payload.whatIfThreatPercent;
    },
    updateAppConfig(state, payload: AppConfig) {
      const defaultConfig: AppConfig['helpConfig'] = {
        url: '',
        tooltip: undefined,
        target: '_blank',
      };
      Object.assign(state.appConfig.helpConfig, defaultConfig, payload.helpConfig);
      Object.assign(state.appConfig.loginConfig, defaultConfig, payload.loginConfig);
      state.appConfig.pagesConfig.splice.apply(
        state.appConfig.pagesConfig,
        [0, state.appConfig.pagesConfig.length, ...payload.pagesConfig],
      );
    },
  },
  actions: {
    syncAppConfig({ commit }): Promise<void> {
      return appConfigService.get()
        .then((appConfig) => {
          commit('updateAppConfig', appConfig);
        });
    },
  },
} as Module<CoreStoreState, RootStoreState>;
